import { DefaultWidgetNames, WidgetSlotConfiguration, WidgetType } from '@/api/rest/resources/types/widget';
import { useStocksSummary } from '@/pages/shared/hooks/useStocksSummary';
import { isNullish } from '@/utils/booleans';

type StockCardType = 'issued' | 'held' | 'sold';
type StockCardVariant = 'count' | 'value';

type StockCardConfig = {
  type: StockCardType;
  variant: StockCardVariant;
};

const CARD_NAME_MAP: Record<StockCardType, Record<StockCardVariant, DefaultWidgetNames>> = {
  issued: {
    count: DefaultWidgetNames.default_total_stocks_issued_card,
    value: DefaultWidgetNames.default_total_stocks_issued_value_card,
  },
  held: {
    count: DefaultWidgetNames.default_total_stocks_held_card,
    value: DefaultWidgetNames.default_total_stocks_held_value_card,
  },
  sold: {
    count: DefaultWidgetNames.default_total_stocks_sold_card,
    value: DefaultWidgetNames.default_total_stocks_sold_value_card,
  },
};

const I18N_PATH_MAP: Record<StockCardType, Record<StockCardVariant, { title: string; tooltip: string }>> = {
  issued: {
    count: {
      title: 'shared.stocks.cards.totalStocksIssued',
      tooltip: 'shared.stocks.cards.totalStocksIssuedTooltip',
    },
    value: {
      title: 'shared.stocks.cards.totalStocksIssuedValue',
      tooltip: 'shared.stocks.cards.totalStocksIssuedValueTooltip',
    },
  },
  held: {
    count: {
      title: 'shared.stocks.cards.totalStocksHeld',
      tooltip: 'shared.stocks.cards.totalStocksHeldTooltip',
    },
    value: {
      title: 'shared.stocks.cards.totalStocksHeldValue',
      tooltip: 'shared.stocks.cards.totalStocksHeldValueTooltip',
    },
  },
  sold: {
    count: {
      title: 'shared.stocks.cards.totalStocksSold',
      tooltip: 'shared.stocks.cards.totalStocksSoldTooltip',
    },
    value: {
      title: 'shared.stocks.cards.totalStocksSoldValue',
      tooltip: 'shared.stocks.cards.totalStocksSoldValueTooltip',
    },
  },
};

const DATA_KEY_MAP: Record<StockCardType, Record<StockCardVariant, string>> = {
  issued: {
    count: 'total_number_stocks_issued',
    value: 'total_value_stocks_issued',
  },
  held: {
    count: 'total_number_stocks_held',
    value: 'total_value_stocks_held',
  },
  sold: {
    count: 'total_number_stocks_sold',
    value: 'total_value_stocks_sold',
  },
};

const createStockCardWidget = (
  config: StockCardConfig,
  stockValue: number | undefined,
  currencyCode?: string,
): WidgetSlotConfiguration => {
  const { type, variant } = config;

  const cardName = CARD_NAME_MAP[`${type}`][`${variant}`];
  const i18nPaths = I18N_PATH_MAP[`${type}`][`${variant}`];

  const baseName = `total_stocks_${type}${variant === 'value' ? '_value' : ''}`;

  const isValueCard = variant === 'value';

  return {
    name: cardName,
    type: WidgetType.o_card,
    visible: true,
    relatedData: {
      styles: {
        root: 'min-w-[200px] grow lg:basis-1/3 rounded-3xl',
        body: 'flex flex-col gap-4 p-8',
      },
    },
    widgets: [
      {
        name: `${baseName}_header`,
        type: WidgetType.a_text,
        visible: true,
        widgets: [
          {
            name: `${baseName}_header_text`,
            type: WidgetType.a_text,
            visible: true,
            relatedData: {
              i18nPath: i18nPaths.title,
              className: 'typography-body1 text-text-primary',
            },
          },
          {
            name: `${baseName}_header_tooltip`,
            type: WidgetType.m_tooltip,
            visible: true,
            relatedData: {},
            widgets: [
              {
                name: `${baseName}_header_tooltip_trigger`,
                type: WidgetType.m_tooltip_trigger,
                visible: true,
                widgets: [
                  {
                    name: `${baseName}_header_tooltip_trigger_icon`,
                    type: WidgetType.a_icon,
                    visible: true,
                    relatedData: {
                      icon: 'ri-information-line',
                      className: 'ml-1 text-active-54',
                    },
                  },
                ],
              },
              {
                name: `${baseName}_header_tooltip_content`,
                type: WidgetType.m_tooltip_content,
                visible: true,
                widgets: [
                  {
                    name: `${baseName}_tooltip_text`,
                    type: WidgetType.a_text,
                    visible: true,
                    relatedData: {
                      i18nPath: i18nPaths.tooltip,
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      // Value display widget - differs based on variant
      isValueCard
        ? {
            name: `${baseName}_amount`,
            type: WidgetType.m_currency,
            visible: !isNullish(stockValue),
            relatedData: {
              value: stockValue,
              currency: currencyCode,
              className: 'typography-h2 text-text-primary',
            },
          }
        : {
            name: `${baseName}_count`,
            type: WidgetType.a_text,
            visible: !isNullish(stockValue),
            relatedData: {
              className: 'typography-h2 text-text-primary',
              text: stockValue,
            },
          },
    ],
  } as WidgetSlotConfiguration;
};

export const useStockCard = (type: StockCardType, variant: StockCardVariant): WidgetSlotConfiguration => {
  const { data } = useStocksSummary();

  const dataKey = DATA_KEY_MAP[`${type}`][`${variant}`] as keyof typeof data.stock_summary;
  const stockValue = data.stock_summary[`${dataKey}`];

  return createStockCardWidget({ type, variant }, stockValue as number | undefined, data.currency_code);
};

// Convenience hooks for direct usage in widget component map
export const useTotalStocksIssued = () => useStockCard('issued', 'count');
export const useTotalStocksHeld = () => useStockCard('held', 'count');
export const useTotalStocksSold = () => useStockCard('sold', 'count');
export const useTotalStocksIssuedValue = () => useStockCard('issued', 'value');
export const useTotalStocksHeldValue = () => useStockCard('held', 'value');
export const useTotalStocksSoldValue = () => useStockCard('sold', 'value');
